import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { Spin, Button, DatePicker } from 'antd';
import dayjs, { type Dayjs } from 'dayjs';
import { type AppDispatch } from '../../redux/store';
import { getReportsAction, viewReportsAction } from '../Loan/store/loanActions';
import CashBookDisplay from '../Reports/CashBookDisplay';

const { RangePicker } = DatePicker;

/**
 * Cash Bank Book for cashiers, who do not get the Reports tab. Only the date range
 * is selectable — the branch is the one they logged in with, and the server pins it
 * again for this role, so another branch's book cannot be pulled from here.
 */
export const CashBankBookIndex = () => {
  const dispatch = useDispatch<AppDispatch>();

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [branch, setBranch] = useState<string>('');
  const [branchCode, setBranchCode] = useState<string>('');
  const [startDate, setStartDate] = useState<string>('');
  const [endDate, setEndDate] = useState<string>('');
  const [dateRange, setDateRange] = useState<
    [Dayjs | null, Dayjs | null] | null
  >(null);
  const [cashBookRows, setCashBookRows] = useState<any>([]);

  useEffect(() => {
    // login sends a cashier only their own branch
    const branches = localStorage.getItem('branches');
    if (branches != null) {
      const parsedBranches = JSON.parse(branches);
      if (parsedBranches?.length > 0) {
        setBranch(String(parsedBranches[0].id));
        setBranchCode(String(parsedBranches[0].code));
      }
    }
  }, []);

  const dateFormat = 'YYYY-MM-DD';

  const onChangeDate = (
    values: [Dayjs | null, Dayjs | null] | null,
    formatString: [string, string]
  ) => {
    setDateRange(values);
    setStartDate(formatString[0]);
    setEndDate(formatString[1]);
    setCashBookRows([]);
  };

  const disabledDate = (current: Dayjs) => current.isAfter(dayjs(), 'day');

  const isDateMissing = startDate === '' || endDate === '';

  const downloadReport = async (): Promise<void> => {
    setIsLoading(true);
    await getReportsAction(dispatch, {
      branch,
      type: 'cashbook',
      startDate,
      endDate,
    });
    setIsLoading(false);
  };

  const viewReport = async (): Promise<void> => {
    setIsLoading(true);
    const response = await viewReportsAction(dispatch, {
      branch,
      type: 'cashbook',
      startDate,
      endDate,
    });
    if (response != null) {
      setCashBookRows(response.responseData);
    }
    setIsLoading(false);
  };

  return (
    <div className="loan__recovery__index__page">
      <Spin spinning={isLoading} fullscreen />
      <div className="title">
        <h1>Cash Bank Book</h1>
      </div>
      <div
        style={{
          display: 'flex',
          flexDirection: 'row',
          gap: 20,
          flexWrap: 'wrap',
        }}
      >
        <div style={{ display: 'flex', flexDirection: 'column' }}>
          <h4>Date</h4>
          <RangePicker
            className="rangepicker"
            value={dateRange}
            onChange={onChangeDate}
            disabledDate={disabledDate}
            format={dateFormat}
          />
        </div>
        <div style={{ display: 'flex', alignItems: 'flex-end', gap: 16 }}>
          <Button
            disabled={isDateMissing}
            onClick={downloadReport}
            className="generate__button"
          >
            <span>Download Report</span>
          </Button>
          <Button
            disabled={isDateMissing}
            onClick={viewReport}
            className="generate__button"
          >
            <span>View Report</span>
          </Button>
        </div>
      </div>
      {branchCode.length > 0 && (
        <p style={{ marginTop: 12 }}>Branch: {branchCode}</p>
      )}
      {cashBookRows?.length > 0 && (
        <div style={{ marginTop: 24 }}>
          <CashBookDisplay data={cashBookRows} />
        </div>
      )}
    </div>
  );
};

export default CashBankBookIndex;
